import { useEffect, useRef, useState } from "react";
import { createGlowStyle } from "@/lib/glowUtils";

// Authoritative cycle constants — must match the backend's HANGAR_* values exactly.
// Reference epoch: 2026-06-23T19:16:18Z — updated 2026-06-23 to reflect the in-game
// cycle time alteration (previously 2026-05-23T13:17:10.315Z).
// The modulo arithmetic requires a GREEN-phase start time as the reference so that
// timeInCycle=0 corresponds to the moment the hangar opens. Using an OPEN→CLOSED
// transition would offset every calculation by the full GREEN_SEC duration.
const HANGAR_REFERENCE_EPOCH_MS = 1782249081000; // 2026-06-23T21:11:21Z in ms
// Corrected 2026-06-04: previous values (11100.129 / 3900.045) were wrong and caused ~8 s drift.
// Derived from xyxyll: CYCLE_DURATION = (65+120)*60*1000 + 226 ms = 11100.226 s
//                      OPEN_DURATION  = Math.round(11100.226 * (65*60*1000) / (185*60*1000)) = 3900.079 s
const GREEN_SEC = 3900.079;   // hangar OPEN duration (seconds) — xyxyll OPEN_DURATION
const CYCLE_SEC = 11100.226;  // full open/closed cycle (seconds) — xyxyll CYCLE_DURATION

// Derive the current phase and remaining time purely from the client clock.
// Both values are recomputed on every call, so two clients at the same UTC
// instant will always produce identical results — no drift, no desync.
function computeFromNow() {
  const nowMs = Date.now();
  const elapsedRaw = (nowMs - HANGAR_REFERENCE_EPOCH_MS) / 1000;
  // Normalise into [0, CYCLE_SEC) — handles dates before the epoch too.
  const timeInCycle = ((elapsedRaw % CYCLE_SEC) + CYCLE_SEC) % CYCLE_SEC;

  if (timeInCycle < GREEN_SEC) {
    // GREEN phase — hangar is open
    const remaining = GREEN_SEC - timeInCycle;
    return {
      phase: "green",
      remaining,
      progressPct: (timeInCycle / CYCLE_SEC) * 100,
      nextLabel: "Closes in",
    };
  } else {
    // RED phase — hangar is closed
    const remaining = CYCLE_SEC - timeInCycle;
    return {
      phase: "red",
      remaining,
      progressPct: (timeInCycle / CYCLE_SEC) * 100,
      nextLabel: "Opens in",
    };
  }
}

function pad(n) {
  return n.toString().padStart(2, "0");
}

function fmt(secs) {
  secs = Math.round(secs);
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

export default function ExecHangarStatus() {
  // Single tick counter — incremented every second to trigger a re-render.
  // The actual phase/remaining values are always recomputed fresh from Date.now().
  const [, setTick] = useState(0);
  const lastPhaseRef = useRef(null);

  // Tick every second — the actual values are always derived fresh from Date.now().
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Compute current phase and remaining time deterministically from the client clock.
  const status = computeFromNow();

  // Update the browser tab title whenever the phase changes.
  useEffect(() => {
    const emoji = status.phase === "green" ? "🟢" : "🔴";
    const label = status.phase === "green" ? "GREEN" : "RED";
    document.title = `[${emoji} ${label}] EXEC/HANGAR`;
    return () => {
      document.title = "EXEC/HANGAR";
    };
  }, [status.phase]);

  // Track phase transitions (for future use — e.g. flash animations or toasts).
  useEffect(() => {
    lastPhaseRef.current = status.phase;
  }, [status.phase]);

  const isGreen = status.phase === "green";
  const isRed = status.phase === "red";

  const dotClass = isGreen
    ? "bg-emerald-300 shadow-[0_0_6px_rgba(52,211,153,1.0),0_0_12px_rgba(52,211,153,1.0),0_0_20px_rgba(52,211,153,1.0)]"
    : isRed
    ? "bg-orange-500 shadow-[0_0_6px_rgba(217,119,6,1.0),0_0_12px_rgba(217,119,6,1.0),0_0_20px_rgba(217,119,6,1.0)]"
    : "bg-slate-600";

  const containerBorder = isGreen
    ? "border-emerald-500/50"
    : isRed
    ? "border-orange-600/50"
    : "border-slate-700";

  const phaseText = isGreen ? "OPEN" : isRed ? "CLOSED" : "Not Synced";
  const phaseTextClass = isGreen
    ? "text-emerald-300"
    : isRed
    ? "text-orange-300"
    : "text-slate-400";

  const timerClass = isGreen
    ? "text-emerald-300"
    : isRed
    ? "text-orange-300"
    : "text-slate-500";

  return (
    <div
      className={`relative flex items-stretch border ${containerBorder} bg-[#121822]/80 transition-colors duration-300 corner-tl corner-tr corner-bl corner-br shadow-[0_0_20px_rgba(6,182,212,0.08)_inset]`}
      data-testid="exec-hangar-status"
      title={`${status.nextLabel} ${fmt(status.remaining)} · Cycle ${Math.round(status.progressPct)}%`}
    >
      {/* Left label */}
      <div className="hidden sm:flex flex-col items-start justify-center px-3 border-r border-slate-800/80">
        <div className="text-[11px] tracking-[0.3em] uppercase text-slate-500 leading-none">
          Exec Hangar
        </div>
        <div className={`text-[11px] tracking-[0.25em] uppercase font-bold ${phaseTextClass} mt-1 leading-none`}>
          {phaseText}
        </div>
      </div>

      {/* Dot + timer */}
      <div className="flex items-center gap-2.5 px-3 py-2">
        <div className={`w-2.5 h-2.5 rounded-full ${dotClass} transition-colors`} aria-hidden />
        <div
          className={`font-dseg7 tabular-nums tracking-wider text-base sm:text-lg text-slate-200`}
          data-testid="exec-hangar-remaining"
          style={{ textShadow: createGlowStyle(timerClass) }}
        >
          {fmt(status.remaining)}
        </div>
        <div className="hidden md:flex flex-col text-[11px] tracking-[0.25em] uppercase ml-1 leading-tight">
          <span className="text-slate-500">{status.nextLabel}</span>
        </div>
      </div>

      {/* Bottom progress sliver */}
      <div 
        className="absolute left-0 right-0 bottom-0 h-[2px] bg-slate-800/40"
      >
        <div
          className={`h-full transition-[width] duration-1000`}
          style={{ 
            width: `${status.progressPct}%`,
            background: isGreen ? 'linear-gradient(135deg, rgba(52,211,153,0.9), rgba(16,185,129,1))' : isRed ? 'linear-gradient(135deg, rgba(217,119,6,0.9), rgba(234,88,12,1))' : 'rgb(71,85,105)',
            boxShadow: isGreen ? '0 0 6px rgba(52,211,153,1.0)' : isRed ? '0 0 6px rgba(217,119,6,1.0)' : 'none'
          }}
        />
      </div>
    </div>
  );
}

